import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { Component, Input, TemplateRef } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { faEllipsisV, faInfoCircle, faTrashCan } from '@fortawesome/free-solid-svg-icons';
import { DxSortableTypes } from 'devextreme-angular/ui/sortable';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { BehaviorSubject, map, Observable } from 'rxjs';
import { ZeroxCustomStore } from 'src/app/shared/services/commons.service';
import { MessagesService } from 'src/app/shared/services/messages.service';
import { MessagingService } from 'src/app/shared/services/messaging.service';
import { StatusService } from 'src/app/shared/services/status.service';
import { environment } from 'src/environments/environment';
import { ControlsOf, TranslateWithFallbackPipe } from 'acl-cloud-lib';
import { Message, MessageService, MessageTypeEnum, Relation, RelationSubjectTypeEnum, Status } from 'src/api/generated';

export interface StatusResponse {
  data: Status[];
}

export interface MessageResponse {
  data: Message[];
}
@Component({
  selector: '[app-messages-container]',
  templateUrl: './messages-container.component.html',
  styleUrl: './messages-container.component.scss'
})
export class MessagesContainerComponent {
  faTrashCan = faTrashCan;
  faInfoCircle = faInfoCircle;
  faEllipsisV = faEllipsisV;
  private _baseUrl: string = environment.baseUrl + "/Status";
  private _msgBaseUrl: string = environment.baseUrl + "/Message";
  modalRef?: BsModalRef;
  private _msg?: Message;
  public isNewMessage: boolean = false;
  private __ROOT__: string = "__ROOT__";
  private _messages: BehaviorSubject<Message[]> = new BehaviorSubject<Message[]>([]);
  public messages$: Observable<Message[]> = this._messages.asObservable();
  private _parent: string = "";
  private _statusName: string = '';

  get Msg(): Message | undefined {
    return this._msg;
  }

  set Msg(value: Message | undefined) {
    this._msg = value;
  }

  public frmData: FormGroup;
  public msgFrmData: FormGroup;

  statuses: Status[] = [];
  lists: Message[][] = [];

  get Parent(): string {
    return this._parent;
  }

  @Input() set Parent(value: string) {
    if (this._parent !== value) {
      if (value == this.__ROOT__) {
        value = "000000000000000000000000";
      }
      this._parent = value;
      this.refreshMessages();
    }
  }

  constructor(
    private _msgSvc: MessagingService,
    private _modalService: BsModalService,
    private _msgsSvc: MessagesService,
    private readonly _messageSvc: MessageService,
    private readonly _statusSvc: StatusService,
    private _http: HttpClient,
    private readonly _translate: TranslateWithFallbackPipe,
    fb: FormBuilder
  ) {
    this.frmData = fb.group<ControlsOf<Status>>({
      Id: new FormControl('', {nonNullable: true}),
      Name: new FormControl('', {nonNullable: true, validators: [Validators.required]}),
      Version: new FormControl(0, {nonNullable: true}),
      Tags: new FormControl([], {nonNullable: true}),
      Created: new FormControl(new Date(), {nonNullable: true}),
      Modified: new FormControl(new Date(), {nonNullable: true}),
      SourceId: new FormControl('', {nonNullable: true, validators: [Validators.required]}),
    });

    this.msgFrmData = fb.group<ControlsOf<Message>>({
      Id: new FormControl('', {nonNullable: true}),
      Name: new FormControl('', {nonNullable: true, validators: [Validators.required]}),
      Version: new FormControl(0, {nonNullable: true}),
      Tags: new FormControl([], {nonNullable: true}),
      Created: new FormControl(new Date(), {nonNullable: true}),
      Modified: new FormControl(new Date(), {nonNullable: true}),
      SourceId: new FormControl('', {nonNullable: true, validators: [Validators.required]}),
      MessageType: new FormControl(MessageTypeEnum.None, {nonNullable: true}),
      MessageContent: new FormControl('', {nonNullable: true, validators: [Validators.required]}),
      ParentId: new FormControl('', {nonNullable: true, validators: [Validators.required]}),
      Attachments: new FormControl([], {nonNullable: true}),
      Destinators: new FormArray([fb.group<ControlsOf<Relation>>({
        SubjectId: new FormControl('', {nonNullable: true}),
        Description: new FormControl('', {nonNullable: true}),
        SubjectType: new FormControl(RelationSubjectTypeEnum.None, {nonNullable: true}),
      })]),
      MessageSource: fb.group<ControlsOf<Relation>>({
        SubjectId: new FormControl('', {nonNullable: true}),
        Description: new FormControl('', {nonNullable: true}),
        SubjectType: new FormControl(RelationSubjectTypeEnum.None, {nonNullable: true}),
      }),
      Status: new FormControl('', {nonNullable: true}),
    });
  }

  ngOnInit(): void {
    this._http.get<StatusResponse>(`${this._baseUrl}/list`).subscribe({
      next: res => {
        if (Array.isArray(res.data)) {
          res.data.forEach(status => {
            this.statuses.push(status);
            this._messageSvc.getList().subscribe({
              next: (response: any) => {
                if (Array.isArray(response.data)) {
                  let data = response.data.filter((msg: any) => msg.Status == status.Name)
                  if(data.length > 0) {
                    this.lists.push(data);
                  } else {
                    this.lists.push([]);
                  }
                }
              },
              error: (err: HttpErrorResponse) => {
                this._msgsSvc.ShowError(this._translate.transform('failed-to-load-messages'));
              }
            });
          });
        }
      }
    });
  }

  public get MsgSvc(): MessagingService {
    return this._msgSvc;
  }

  public get StatusSvc(): StatusService {
    return this._statusSvc;
  }

  private refreshMessages(): void {
    var store = this.MsgSvc.dataSource.store() as ZeroxCustomStore<Message, string>
    if (store.loadAsObservable != undefined) {
      store.loadAsObservable({
        filter: ["ParentId", "=", this._parent]
      }).subscribe({
        next: (data: any) => {
          this._messages.next(data.data)
        }
      })
    }
  }

  onListReorder(e: DxSortableTypes.ReorderEvent) {
    const list = this.lists.splice(e.fromIndex, 1)[0];
    this.lists.splice(e.toIndex, 0, list);

    const status = this.statuses.splice(e.fromIndex, 1)[0];
    this.statuses.splice(e.toIndex, 0, status);
  }

  onTaskDragStart(e: DxSortableTypes.DragStartEvent) {
    e.itemData = e.fromData[e.fromIndex];
  }

  onTaskDrop(e: DxSortableTypes.AddEvent | DxSortableTypes.ReorderEvent) {
    e.fromData.splice(e.fromIndex, 1);
    e.toData.splice(e.toIndex, 0, e.itemData);
    this.statuses.forEach((status, index) => {
      this.lists[index].forEach(msg => {
        if(msg.Status != status.Name) {
          msg.Status = status.Name;
          if(!Array.isArray(msg.Attachments)) {
            msg.Attachments = [];
          }

          this._http.put<any>(`${this._msgBaseUrl}`, msg, {
            headers: new HttpHeaders().set('Content-Type', 'application/json')
          }).subscribe({
            next: () => {
              setTimeout(() => {
              }, 200);
            },
            error: () => {
              setTimeout(() => {
                this._msgsSvc.ShowError(this._translate.transform('failed-to-save-board'));
              }, 200);
            }
          });
        }
      })
    })
  }

  openModal(template: TemplateRef<any>, status: string | null | undefined, msg: Message | undefined) {
    this._msg = msg;
    this._statusName = status ?? '';
    this.modalRef = this._modalService.show(template);
  }

  decline(): void {
    this.modalRef?.hide();
  }

  public save(): void {
    if (this.frmData.valid) {
      this._statusSvc.SaveStatus(this.frmData.value);
      this.frmData.markAsPristine();
      this.decline();
    } else {
      this._msgsSvc.ShowError(this._translate.transform('invalid-data'));
    }
  }

  public saveMsg(id: string | undefined): void {
    this.msgFrmData.controls['Status'].patchValue(this._statusName);
    this.msgFrmData.controls["ParentId"].patchValue("000000000000000000000000");
    if (this.msgFrmData.valid) {
      this._msgSvc.SaveMessage(this.msgFrmData.value);
      this.msgFrmData.markAsPristine();
      this.decline();
    } else {
      this._msgsSvc.ShowError(this._translate.transform('invalid-data'));
    }
  }

  public deleteBoard(id: string | undefined): void {
    this._http.delete<number>(`${this._baseUrl}/delete/${id}`).subscribe({
      next: () => {
        window.location.reload();
      },
      error: (err: HttpErrorResponse) => {
        this._msgsSvc.ShowError(this._translate.transform('failed-to-delete-board'));
      }
    });
  }
}
